import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  GetPurchases,
  GetPurchaseById,
  CreatePurchase,
  UpdatePurchase,
  DeletePurchase,
  ScanInvoice,
  ReturnPurchase,
} from "../Api";
import { ServiceQueryOptions, unwrapApiResponse } from "./query_helpers";
import { DEFAULT_STALE_TIME } from "../Constant/AppConstants";

export const purchaseQueryKeys = {
  purchases: (params?: Parameters<typeof GetPurchases>[0]) =>
    ["purchases", params] as const,
  purchaseById: (params?: Parameters<typeof GetPurchaseById>[0]) =>
    ["purchaseById", params] as const,
};

export const invalidatePurchaseQueries = (queryClient: any) => {
  queryClient.invalidateQueries({ queryKey: ["purchases"] });
  queryClient.invalidateQueries({ queryKey: ["purchaseById"] });
};

export const usePurchasesQuery = (
  params: Parameters<typeof GetPurchases>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: purchaseQueryKeys.purchases(params),
    queryFn: async () => {
      const res = await GetPurchases(params);
      if (!res?.success) {
        return { data: [], total: 0 };
      }
      return {
        data: res.data || [],
        total: res.meta?.total || 0,
      };
    },
  });
};

export const usePurchasesRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof GetPurchases>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: purchaseQueryKeys.purchases(params),
        queryFn: async () => {
          const res = await GetPurchases(params);
          if (!res?.success) {
            return { data: [], total: 0 };
          }
          return {
            data: res.data || [],
            total: res.meta?.total || 0,
          };
        },
      }),
  });
};

export const usePurchaseByIdQuery = (
  params: Parameters<typeof GetPurchaseById>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: purchaseQueryKeys.purchaseById(params),
    queryFn: async () => unwrapApiResponse(await GetPurchaseById(params)),
  });
};

export const usePurchaseByIdRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof GetPurchaseById>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: purchaseQueryKeys.purchaseById(params),
        queryFn: async () => unwrapApiResponse(await GetPurchaseById(params)),
      }),
  });
};

export const useCreatePurchaseMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof CreatePurchase>[0]) =>
      CreatePurchase(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidatePurchaseQueries(queryClient);
      }
    },
  });
};

export const useUpdatePurchaseMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof UpdatePurchase>[0]) =>
      UpdatePurchase(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidatePurchaseQueries(queryClient);
      }
    },
  });
};

export const useDeletePurchaseMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof DeletePurchase>[0]) =>
      DeletePurchase(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidatePurchaseQueries(queryClient);
      }
    },
  });
};

export const useScanInvoiceMutation = () => {
  return useMutation({
    mutationFn: (payload: {
      base64Data: Parameters<typeof ScanInvoice>[0];
      mimeType: Parameters<typeof ScanInvoice>[1];
    }) => ScanInvoice(payload.base64Data, payload.mimeType),
  });
};

export const useReturnPurchaseMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => ReturnPurchase(id),
    onSuccess: (res) => {
      if (res?.success) {
        invalidatePurchaseQueries(queryClient);
      }
    },
  });
};

export const usePurchaseItemsRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number | string) => {
      const res = await queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: purchaseQueryKeys.purchaseById(id),
        queryFn: async () => unwrapApiResponse(await GetPurchaseById(id)),
      });

      return res?.items || [];
    },
  });
};

export const usePurchaseService = (config?: {
  fetchPurchasesParams?: Parameters<typeof GetPurchases>[0];
  fetchPurchasesEnabled?: boolean;
  fetchPurchaseByIdParams?: Parameters<typeof GetPurchaseById>[0];
  fetchPurchaseByIdEnabled?: boolean;
}) => {
  const purchasesQuery = usePurchasesQuery(config?.fetchPurchasesParams, {
    enabled:
      config?.fetchPurchasesEnabled !== false &&
      config?.fetchPurchasesParams !== undefined &&
      config?.fetchPurchasesParams !== null,
  });

  const purchaseByIdQuery = usePurchaseByIdQuery(
    config?.fetchPurchaseByIdParams,
    {
      enabled:
        config?.fetchPurchaseByIdEnabled !== false &&
        config?.fetchPurchaseByIdParams !== undefined &&
        config?.fetchPurchaseByIdParams !== null,
    },
  );

  const createPurchaseMutation = useCreatePurchaseMutation();
  const updatePurchaseMutation = useUpdatePurchaseMutation();
  const deletePurchaseMutation = useDeletePurchaseMutation();
  const scanInvoiceMutation = useScanInvoiceMutation();

  return {
    purchasesQuery,
    purchaseByIdQuery,
    createPurchaseMutation,
    updatePurchaseMutation,
    deletePurchaseMutation,
    scanInvoiceMutation,
  };
};
